import Form from 'antd/es/form';
import Input from 'antd/es/input';
import React from 'react';

import type { PipelineBlock, SemanticFilterBlockConfig } from '@mai/types';

import { NumberRange } from './shared';

interface SemanticFilterBlockEditorProps {
  block: PipelineBlock & { type: 'semantic_filter' };
  onConfigChange: (config: SemanticFilterBlockConfig) => void;
}

export const SemanticFilterBlockEditor: React.FC<
  SemanticFilterBlockEditorProps
> = ({ block, onConfigChange }) => {
  const [form] = Form.useForm();

  // Initialize form with current values
  React.useEffect(() => {
    form.setFieldsValue({
      embedding_model:
        block.config.embedding_model ||
        'sentence-transformers/static-retrieval-mrl-en-v1',
      filter_strategy:
        block.config.filter_strategy || 'cosine_similarity_top_k',
      top_k: block.config.top_k || 10,
      similarity_threshold: block.config.similarity_threshold || 0,
      batch_size: block.config.batch_size || 100,
    });
  }, [block.config, form]);

  const handleValuesChange = (
    _: unknown,
    allValues: Partial<SemanticFilterBlockConfig>,
  ) => {
    onConfigChange({
      ...block.config,
      embedding_model:
        allValues.embedding_model ||
        'sentence-transformers/static-retrieval-mrl-en-v1',
      filter_strategy: 'cosine_similarity_top_k', // Fixed value
      top_k: allValues.top_k || 10,
      similarity_threshold: allValues.similarity_threshold || 0,
      batch_size: allValues.batch_size || 100,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      size="small"
      onValuesChange={handleValuesChange}
    >
      <Form.Item
        label="Embedding Model"
        name="embedding_model"
        help="Model to use for generating embeddings"
      >
        <Input placeholder="sentence-transformers/static-retrieval-mrl-en-v1" />
      </Form.Item>

      <NumberRange
        label="Top K"
        name="top_k"
        min={1}
        max={1000}
        help="Number of top results to return"
      />

      <NumberRange
        label="Similarity Threshold"
        name="similarity_threshold"
        min={0}
        max={1}
        step={0.01}
        help="Minimum similarity score for inclusion (0.0 to 1.0)"
      />

      <NumberRange
        label="Batch Size"
        name="batch_size"
        min={1}
        max={1000}
        help="Number of texts to process in each embedding batch"
      />
    </Form>
  );
};
